import React, { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ACCIDENT_TYPES as INTAKE_ACCIDENT_TYPES, SERVED_STATE_CODES as SERVED_CODES, stateName } from "../data/intake";
import { OhioExampleDisclosure } from "./OhioExampleDisclosure";
import { ArrowRight, Sparkles, ShieldCheck } from "lucide-react";

// Mirror the exact labels used by Apply.jsx step 4 so the URL prefill auto-selects.
const ACCIDENT_TYPES = INTAKE_ACCIDENT_TYPES;

const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI",
  "MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT",
  "VT","VA","WA","WV","WI","WY",
];

const RATE = 0.038; // 3.8% monthly
const TIER_MONTHS = 6;
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

export default function PreQualWidget() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(5000);
  const [accident, setAccident] = useState(ACCIDENT_TYPES[0]);
  const [stateAbbr, setStateAbbr] = useState("OH");

  const payoff = useMemo(() => {
    const six = amount * Math.pow(1 + RATE, TIER_MONTHS);
    const twelve = amount * Math.pow(1 + RATE, TIER_MONTHS * 2);
    return {
      sixMonths: Math.round(six),
      twelveMonths: Math.round(twelve),
      tierPct: Math.round((Math.pow(1 + RATE, TIER_MONTHS) - 1) * 100),
    };
  }, [amount]);

  const continueToApply = () => {
    const params = new URLSearchParams({
      amount: String(amount),
      accident_type: accident,
      state: stateAbbr,
    });
    navigate(`/apply?${params.toString()}`);
  };

  return (
    <div className="bg-white text-[#0A2540] rounded-2xl shadow-2xl border border-white/30 overflow-hidden" data-testid="pre-qual-widget">
      <div className="p-6 lg:p-7 space-y-5">
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#0066FF] font-semibold">
            <Sparkles className="w-3.5 h-3.5" /> 60-second pre-qual
          </div>
          <span className="text-xs text-[#6B7280]">No credit check · No SSN required</span>
        </div>

        {/* Amount slider */}
        <div>
          <div className="flex items-baseline justify-between">
            <label className="text-sm font-medium text-[#374151]">Amount you need</label>
            <span className="font-display text-2xl font-semibold text-[#0A2540] tabular-nums">{fmt(amount)}</span>
          </div>
          <Slider
            value={[amount]}
            min={500}
            max={25000}
            step={500}
            onValueChange={(v) => setAmount(v[0])}
            className="mt-3"
            data-testid="prequal-amount-slider"
          />
          <div className="flex justify-between text-xs text-[#9CA3AF] mt-1.5">
            <span>$500</span><span>$25,000</span>
          </div>
        </div>

        {/* Accident type chips */}
        <div>
          <label className="text-sm font-medium text-[#374151]">Type of case</label>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {ACCIDENT_TYPES.map((t) => (
              <button
                key={t}
                onClick={() => setAccident(t)}
                data-testid={`prequal-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-3 h-8 rounded-full text-xs font-medium border transition-colors ${
                  accident === t
                    ? "bg-[#0A2540] text-white border-[#0A2540]"
                    : "bg-white text-[#374151] border-[#E5E7EB] hover:border-[#0066FF]"
                }`}
              >{t}</button>
            ))}
          </div>
        </div>

        {/* State */}
        <div>
          <label className="text-sm font-medium text-[#374151]">State</label>
          <Select value={stateAbbr} onValueChange={setStateAbbr}>
            <SelectTrigger className="mt-2" data-testid="prequal-state"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Estimate readout: Ohio model shown only for served states; never for unserved states. */}
        {SERVED_CODES.includes(stateAbbr) ? (
        <div className="rounded-xl bg-[#F6F9FC] border border-[#E5E7EB] p-4" data-testid="prequal-estimate">
          <div className="text-xs uppercase tracking-widest text-[#6B7280] mb-2" data-testid="prequal-estimate-label">
            {stateAbbr === "OH" ? "Illustrative Ohio Funding Estimate" : "Illustrative Ohio Funding Example"}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-display text-xl font-semibold text-[#0A2540] tabular-nums">{fmt(payoff.sixMonths)}</div>
              <div className="text-xs text-[#6B7280] mt-0.5">if settled in 6 months</div>
            </div>
            <div>
              <div className="font-display text-xl font-semibold text-[#0A2540] tabular-nums">{fmt(payoff.twelveMonths)}</div>
              <div className="text-xs text-[#6B7280] mt-0.5">if settled in 12 months</div>
            </div>
          </div>
          <OhioExampleDisclosure stateAbbr={stateAbbr} className="text-xs text-[#6B7280] mt-3 leading-relaxed" testid="prequal-disclosure" />
        </div>
        ) : (
        <div className="rounded-xl bg-[#F6F9FC] border border-[#E5E7EB] p-4 text-sm text-[#374151] leading-relaxed" data-testid="prequal-unserved-notice">
          Instabridge does not currently offer funding in {stateName(stateAbbr)}. We're continually working to expand our service areas — contact us to learn about future availability.
        </div>
        )}

        {SERVED_CODES.includes(stateAbbr) ? (
        <Button onClick={continueToApply} className="w-full btn-primary-navy h-12 rounded-lg text-base" data-testid="prequal-continue-btn">
          Continue your application <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        ) : (
        <Link to="/contact" className="block">
          <Button className="w-full btn-primary-navy h-12 rounded-lg text-base" data-testid="prequal-contact-btn">
            Contact us <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
        )}

        <div className="flex items-center justify-center gap-1.5 text-xs text-[#6B7280]">
          <ShieldCheck className="w-3.5 h-3.5 text-[#047857]" /> Encrypted & confidential. Funded in 24–48 hrs if approved.
        </div>
      </div>
    </div>
  );
}
